'use client'

import React, { useState, useRef, MouseEvent, useCallback } from 'react'
import type { Media as MediaType } from '@/payload-types'
import { ZoomIn } from 'lucide-react'
import { Media } from '@/components/Media'

export const GalleryZoomImage = ({ image }: { image: MediaType }) => {
  const [isZoomed, setIsZoomed] = useState(false)
  const [position, setPosition] = useState({ x: 50, y: 50 })
  const containerRef = useRef<HTMLDivElement>(null)

  const updatePosition = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return
    const { left, top, width, height } = containerRef.current.getBoundingClientRect()
    
    // Calculate relative percentage
    const x = ((e.clientX - left) / width) * 100
    const y = ((e.clientY - top) / height) * 100
    
    setPosition({ x, y })
  }, [])

  // All hooks above — safe to early-return below

  if (!image || typeof image !== 'object' || !image.url) {
    return null
  }

  const isVideo = image.mimeType?.includes('video')

  if (isVideo) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Media
          resource={image}
          className="h-full w-full"
          videoClassName="w-full h-full object-contain"
        />
      </div>
    )
  }

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (isZoomed) {
      updatePosition(e)
    }
  }

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!isZoomed) {
      updatePosition(e)
    }
    setIsZoomed(!isZoomed)
  }

  const handleMouseLeave = () => {
    setIsZoomed(false)
  }

  return (
    <div 
      ref={containerRef}
      className={`w-full h-full relative overflow-hidden group ${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        className="w-full h-full flex items-center justify-center transition-transform duration-200 ease-out"
        style={{
          transformOrigin: `${position.x}% ${position.y}%`,
          transform: isZoomed ? 'scale(2.5)' : 'scale(1)',
        }}
      >
        <Media
          resource={image}
          fill
          className="h-full w-full pointer-events-none"
          imgClassName="h-full w-full object-contain pointer-events-none"
          priority
        />
      </div>

      {/* Magnifier glass indicator */}
      <div 
        className={`absolute bottom-4 right-4 bg-black/60 text-white p-2 rounded-full backdrop-blur-sm pointer-events-none transition-opacity duration-300 ${isZoomed ? 'opacity-0' : 'opacity-100'}`}
      >
        <ZoomIn className="w-5 h-5 text-[var(--neon-orange)]" />
      </div>
    </div>
  )
}
